import { toMajorCurrencyUnit, toSmallestCurrencyUnit } from "@tellescope/utils";
import type { Tab } from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { useForm } from "@tellescope/forms";
import { useTellescopeInfiniteQuery, useTellescopeMutation } from "@tellescope/tanstack-query";
import { toast } from "sonner";

export interface UseChargePaymentFormArgs {
  contactId: string;
  onSubmit?: () => void;
}

/**
 * Tab identities, kept separate from the translated labels: these are the values the `type` field
 * holds and that its side effect branches on, so they must not shift with the active locale.
 */
export const PRODUCTS_TAB = "products";
export const CUSTOM_PRICE_TAB = "customPrice";

export const useChargePaymentForm = ({ contactId, onSubmit }: UseChargePaymentFormArgs) => {
  const { t } = useTranslation("payments");

  const stripeAccountsQuery = useTellescopeInfiniteQuery({
    model: "organizations",
    method: "getSome",
    request: {},
    select: (data) => data.pages.flatMap((page) => page.flatMap((org) => org.stripeKeyDetails ?? [])),
  });

  const productsQuery = useTellescopeInfiniteQuery({
    model: "products",
    method: "getSome",
    request: {},
    select: (data) => data.pages.flat(),
  });

  const chargePaymentMutation = useTellescopeMutation({
    model: "purchases",
    method: "charge_card_on_file",
    autoCacheUpdateStrategy: "invalidate",
    onSuccess: () => {
      onSubmit?.();
    },
    onError: () => {
      toast.error(t("There was an error charging the card on file."));
    },
  });

  const tabs: Tab[] = [
    { label: t("Products"), value: PRODUCTS_TAB },
    { label: t("Custom Price"), value: CUSTOM_PRICE_TAB },
  ];

  const hasProducts = (productsQuery.data?.length ?? 0) > 0;

  const sumProductCosts = (productIds: string[]) =>
    toMajorCurrencyUnit(
      productIds.reduce<number>((acc, productId) => {
        const product = productsQuery.data?.find((p) => p.id === productId);
        return acc + (product?.cost?.amount ?? 0);
      }, 0)
    );

  const initialProductIds = productsQuery.data?.length === 1 ? [productsQuery.data[0]?.id ?? ""] : [];

  const form = useForm({
    fields: {
      stripeAccountKey: {
        type: "select",
        initialValue: stripeAccountsQuery.data?.length === 1 ? stripeAccountsQuery.data[0]?.key : "",
        required: true,
      },
      type: {
        type: "select",
        initialValue: hasProducts ? PRODUCTS_TAB : CUSTOM_PRICE_TAB,
        required: true,
      },
      /**
       * Deliberately not `required`: the requirement depends on the active tab, and `validateField`
       * returns the required error before any custom validator runs, so `required` cannot be scoped
       * to a tab. The rule lives in `validators.productIds` instead.
       */
      productIds: {
        type: "multiselect",
        initialValue: initialProductIds,
      },
      cost: {
        type: "number",
        initialValue: hasProducts ? sumProductCosts(initialProductIds) : 0,
        required: true,
      },
      description: {
        type: "text",
        initialValue: "",
      },
    },
    onSubmit: (values) => {
      chargePaymentMutation.mutate({
        enduserId: contactId,
        stripeKey: values.stripeAccountKey,
        productIds: values.type === PRODUCTS_TAB ? values.productIds : undefined,
        cost: {
          currency: "USD",
          amount: toSmallestCurrencyUnit(values.cost),
        },
        description: values.description,
      });
    },
    validators: {
      cost: (value) => (value > 0 ? undefined : t("Cost must be greater than zero")),
      // Only the Products tab submits `productIds`, so requiring a selection while the Custom Price
      // tab is active would leave the form permanently invalid for any org without exactly one
      // product — its untouched `productIds` is `[]`.
      productIds: (value, allValues) =>
        allValues.type === PRODUCTS_TAB && value.length === 0 ? t("Select at least one product") : undefined,
    },
    sideEffects: {
      type: (value, allValues) => ({
        cost: value === CUSTOM_PRICE_TAB ? 0 : sumProductCosts(allValues.productIds),
      }),
      productIds: (value) => ({ cost: sumProductCosts(value) }),
    },
  });

  return {
    form,
    tabs,
    stripeAccounts: stripeAccountsQuery.data,
    products: productsQuery.data,
    // Gates the submit button. `form.isSubmitting` cannot stand in for it: `onSubmit` calls `.mutate`,
    // which returns immediately, so the flag clears a microtask later while the charge is still in
    // flight — long enough for a second click to charge the card again.
    isCharging: chargePaymentMutation.isPending,
  };
};
